// ── The People lanes — one interaction grammar, three separate questions ──────────────────────
//
// ⚠ ONE PICKER AND ONE LABEL MAP ACROSS DETAIL AND BULK. The same question asked in two places
// must not acquire two answers about who is eligible — and a second picker is exactly how the
// rendered vocabulary drifts from the rule the server enforces.
//
// ⚠ AND THE LANES STAY SEPARATE. These components never offer "also set the other one". Assignee,
// Responsible and Approver are three facts about a control; a convenience that wrote two at once
// would collapse a distinction the schema holds deliberately.

import { useState } from 'react';
import {
  CTS_PEOPLE_DETAIL, CTS_PEOPLE_HELP, CTS_PEOPLE_LABEL, ctsLaneEligible, type CtsPeopleLane,
} from '@grc/shared';
import { InfoDot } from './InfoDot';
import { CARD_SURFACE } from './card-surface';
import { cn } from '@/lib/utils';
import type { TeamMember } from '@/api/types/settings.types';

/**
  * ⚠ THE PRIMARY ACTION, ONE STYLE FOR ALL THREE LANES. Naming a person on a control is the point
  * of this whole region, and the button read as an afterthought beside the text it sat under.
  * Shared so the three lanes cannot drift into three different weights of the same act.
  */
export const ASSIGN_BTN =
  'rounded-[7px] bg-accent-cyan px-[13px] py-[7px] text-[12.5px] font-medium text-bg-base '
  + 'transition-colors hover:bg-accent-cyan/90 disabled:opacity-50';

/** ⚠ The SAME surface as every other tile on the page — see ./card-surface for why. */
export const PANEL_CARD = `${CARD_SURFACE} flex h-full flex-col p-[15px]`;

export function memberName(m: TeamMember): string {
  return m.display_name || `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || m.email;
}

/**
 * The eligibility-filtered person picker.
 *
 * ⚠ THE FILTER IS A COURTESY, NOT THE CONTROL. Hiding a name does not refuse an assignment — the
 * api runs the same rule against the database and is the thing that says no. This exists so a
 * person is not offered a choice that will be rejected, which is a different job from enforcement.
 */
export function PeoplePicker({
  lane, team, value, onChange, id,
}: {
  lane: CtsPeopleLane;
  team: TeamMember[] | undefined;
  value: string;
  onChange: (userId: string) => void;
  id: string;
}) {
  const eligible = (team ?? []).filter((m) => ctsLaneEligible(lane, m.roles));
  return (
    <>
      <select
        id={id} value={value} onChange={(e) => onChange(e.target.value)}
        className="rounded-[7px] border border-border-subtle bg-bg-surface px-[9px] py-[6px] text-[12.5px] text-text-primary"
      >
        <option value="">Choose someone…</option>
        {eligible.map((m) => (
          <option key={m.id} value={m.id}>
            {memberName(m)}{m.roles.length > 0 ? ` — ${m.roles.join(', ')}` : ''}
          </option>
        ))}
      </select>
      {eligible.length === 0 && (
        // ⚠ An empty picker states WHY it is empty. "No options" reads as a broken control.
        <p className="text-[11.5px] text-text-faint">
          No one in your organisation is eligible to be {CTS_PEOPLE_LABEL[lane]} on a control.
          View-only, auditor and service accounts are excluded.
        </p>
      )}
    </>
  );
}

/**
 * One RACI lane — Responsible or Approver — on one control.
 *
 * ⚠ IT ALWAYS RENDERS, SET OR NOT. The Approver slot used to appear only when it had a value, so
 * a control with no Approver showed nothing at all and the fact was indistinguishable from the
 * feature not existing. An unset lane is a real state and says so.
 */
export function PeopleLaneCard({
  lane, currentName, currentUserId, sameAsOther, team, busy, onSet, onClear,
}: {
  lane: Exclude<CtsPeopleLane, 'assignee'>;
  currentName: string | null;
  currentUserId: string | null;
  /** True when this person also holds the other RACI lane on this control. */
  sameAsOther: boolean;
  team: TeamMember[] | undefined;
  busy: boolean;
  onSet: (userId: string) => void;
  onClear: () => void;
}) {
  const [picking, setPicking] = useState(false);
  const [pick, setPick] = useState('');
  const label = CTS_PEOPLE_LABEL[lane];

  const reset = () => { setPicking(false); setPick(''); };

  return (
    <div className={PANEL_CARD} data-testid={`people-lane-${lane}`}>
      <div className="flex items-start gap-[8px]">
        <span className="text-[13px] font-semibold text-text-primary">{label}</span>
        <InfoDot label={label} detail={CTS_PEOPLE_DETAIL[lane]} />
      </div>
      {/* The helper line STAYS — the icon adds detail, it does not replace the one-liner. */}
      <p className="mb-[12px] mt-[3px] text-[11px] leading-[1.45] text-text-faint">
        {CTS_PEOPLE_HELP[lane]}
      </p>

      <div className="mb-[10px] flex items-center gap-[10px]">
        <span className={cn('rounded-full px-[9px] py-[2px] text-[11.5px]',
          currentUserId ? 'bg-cert-valid-fg/12 text-cert-valid-fg' : 'bg-text-faint/12 text-text-secondary')}>
          {currentUserId ? 'Set' : 'Not set'}
        </span>
        <span className="text-[13px] text-text-primary">
          {currentName ?? <span className="italic text-text-faint">Nobody named</span>}
        </span>
      </div>

      {sameAsOther && (
        // ⚠ DISPLAY, NOT ENFORCEMENT. The product does not prevent one person holding both lanes,
        //    and this note must never be read as saying it does. It states what is true so a
        //    reviewer can notice it — nothing here claims segregation of duties was required.
        <p className="mb-[10px] text-[11.5px] text-cert-warn-fg">
          The same person is Responsible and Approver on this control.
        </p>
      )}

      {picking ? (
        <div className="flex flex-col gap-[8px]">
          <label className="text-[11.5px] text-text-secondary" htmlFor={`pick-${lane}`}>
            {currentUserId ? `Change ${label}` : `Set ${label}`}
          </label>
          <PeoplePicker id={`pick-${lane}`} lane={lane} team={team} value={pick} onChange={setPick} />
          <div className="flex gap-[8px]">
            <button
              type="button" disabled={busy || !pick}
              onClick={() => { onSet(pick); reset(); }}
              className={ASSIGN_BTN}
            >
              Confirm
            </button>
            <button type="button" onClick={reset} disabled={busy}
              className="rounded-[7px] px-[11px] py-[6px] text-[12px] text-text-secondary">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-[8px]">
          {/* ⚠ "Assign", not "Assign Responsible" — the card title already says which lane this
              is, so repeating it made three lanes read as three different interactions instead of
              one pattern applied three times. */}
          {/* ⚠ The VISIBLE word is "Assign" for all three lanes — the card says which lane. But the
              ACCESSIBLE name stays specific: a screen-reader user hears the buttons out of their
              visual context, and three identical "Assign" buttons on one page is not a choice. */}
          <button
            type="button" onClick={() => setPicking(true)} disabled={busy}
            aria-label={currentUserId ? `Change ${label}` : `Assign ${label}`}
            className={currentUserId
              ? cn('rounded-[7px] border border-border-subtle bg-bg-surface px-[13px] py-[7px] text-[12.5px] font-medium text-text-primary disabled:opacity-50')
              : ASSIGN_BTN}
          >
            {currentUserId ? 'Change' : 'Assign'}
          </button>
          {currentUserId && (
            // ⚠ Clearing is a WRITE that sets the column NULL — never a delete, and audited as a
            //    write, because "who removed the Approver" is exactly the question asked later.
            <button
              type="button" onClick={onClear} disabled={busy}
              className="rounded-[7px] border border-border-subtle bg-bg-surface px-[11px] py-[6px] text-[12px] text-text-secondary disabled:opacity-50"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}
